import { useCallback, useEffect, useRef, useState } from 'react';

import type { AdminTeamDto, AdminTeamMemberDto, UserDto } from '@time-tracking/shared';

import {
  useAddTeamMember,
  useAdminTeamMembers,
  useAdminTeams,
  useCreateTeam,
  useDeleteTeam,
  useRemoveTeamMember,
  useUpdateTeam,
  useUpdateTeamMemberRole,
} from '../../../api/admin.js';
import { useSearchUsers } from '../../../api/users.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

// ─── Avatar helpers ──────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  { bg: '#e8f3e0', fg: '#2a6017', border: '#b8daa0' },
  { bg: '#e3eef5', fg: '#005f86', border: '#b0d0e4' },
  { bg: '#fff6e0', fg: '#7a5a00', border: '#f0d98a' },
  { bg: '#f0e8f3', fg: '#6d3b8e', border: '#d8c0e8' },
  { bg: '#ffe8e3', fg: '#b03000', border: '#f0b8a8' },
  { bg: '#e6ebee', fg: '#333f48', border: '#c0ccd4' },
];

function avatarColor(userId: string) {
  const sum = [...userId].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return (
    AVATAR_COLORS[sum % AVATAR_COLORS.length] ?? { bg: '#e6ebee', fg: '#333f48', border: '#c0ccd4' }
  );
}

// ─── Team Meta Dialog ─────────────────────────────────────────────────────────

const TEAM_COLORS = [
  { hex: '#bf5700', label: 'Burnt Orange' },
  { hex: '#005f86', label: 'Bluebonnet' },
  { hex: '#579d42', label: 'Turtle Pond' },
  { hex: '#00a9b7', label: 'Turquoise' },
  { hex: '#9cadb7', label: 'Shade' },
  { hex: '#333f48', label: 'Charcoal' },
  { hex: '#f8971f', label: 'Tangerine' },
  { hex: '#a6cd57', label: 'Cactus' },
];

interface TeamMetaDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialTeamName?: string;
  initialColor?: string;
  initialLeadUserId?: string | null;
  members?: { userId: string; displayName: string; utEid: string | null }[];
  isPending: boolean;
  onClose: () => void;
  onSubmit: (data: {
    teamName: string;
    color: string;
    leadUserId: string | null;
    revokeOutgoingLeadScope?: boolean;
  }) => void;
}

function TeamMetaDialog({
  open,
  mode,
  initialTeamName = '',
  initialColor = '#9cadb7',
  initialLeadUserId = null,
  members = [],
  isPending,
  onClose,
  onSubmit,
}: TeamMetaDialogProps) {
  const [teamName, setTeamName] = useState(initialTeamName);
  const [color, setColor] = useState(initialColor);
  const [leadUserId, setLeadUserId] = useState<string>(initialLeadUserId ?? '');
  // Defaults to keeping the outgoing lead's approver access: replacing a lead is
  // not, on its own, a decision to strip their approval scope, and silently
  // revoking would be as wrong as silently retaining. The admin chooses.
  const [revokeOutgoingLeadScope, setRevokeOutgoingLeadScope] = useState(false);
  const [touched, setTouched] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');
  // The picked search result as-is, so the chip renders its server-computed initials.
  const [selectedLead, setSelectedLead] = useState<UserDto | null>(null);
  const [leadActiveIndex, setLeadActiveIndex] = useState(-1);
  const [leadOpen, setLeadOpen] = useState(false);
  const leadWrapRef = useRef<HTMLDivElement>(null);
  const leadSearchInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  useFocusTrap(dialogRef, open);

  const leadSearchQuery = useSearchUsers(leadSearch);
  const leadResults = leadSearchQuery.data ?? [];

  useEffect(() => {
    setLeadActiveIndex(-1);
  }, [leadResults]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (leadWrapRef.current && !leadWrapRef.current.contains(e.target as Node)) {
        setLeadOpen(false);
        setLeadActiveIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setTeamName(initialTeamName);
      setColor(initialColor);
      setLeadUserId(initialLeadUserId ?? '');
      setTouched(false);
      setLeadSearch('');
      setSelectedLead(null);
      setLeadActiveIndex(-1);
      setLeadOpen(false);
      return;
    }
    setTeamName(initialTeamName);
    setColor(initialColor);
    setLeadUserId(initialLeadUserId ?? '');
    setRevokeOutgoingLeadScope(false);
    setTimeout(() => firstInputRef.current?.focus(), 0);
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, initialTeamName, initialColor, initialLeadUserId, onClose]);

  if (!open) return null;

  const pickLead = (u: UserDto) => {
    setSelectedLead(u);
    setLeadUserId(u.userId);
    setLeadSearch('');
    setLeadOpen(false);
  };

  // Only when an existing lead is being replaced. Reassigning to the same person,
  // or setting a lead on a team that had none, revokes nothing from anyone.
  const outgoingLead =
    mode === 'edit' && initialLeadUserId && leadUserId !== initialLeadUserId
      ? (members.find((m) => m.userId === initialLeadUserId) ?? null)
      : null;

  const nameError = touched && teamName.trim().length === 0 ? 'Name is required' : null;
  const leadError = touched && mode === 'create' && !leadUserId ? 'Team lead is required' : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!teamName.trim() || isPending) return;
    if (mode === 'create' && !leadUserId) return;
    onSubmit({
      teamName: teamName.trim(),
      color,
      leadUserId: leadUserId || null,
      ...(outgoingLead ? { revokeOutgoingLeadScope } : {}),
    });
  }

  const selectedColorLabel = TEAM_COLORS.find((c) => c.hex === color)?.label ?? 'Custom';

  return (
    <div
      className="dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-meta-dialog-title"
        className="projects-dialog"
      >
        <h2 id="team-meta-dialog-title" className="dialog-title">
          {mode === 'create' ? 'New team' : 'Edit team'}
        </h2>
        <form onSubmit={handleSubmit} className="np-dialog">
          <div className="np-dialog-body">
            <div>
              <label htmlFor="team-name-input">
                Team name <span aria-hidden="true">*</span>
              </label>
              <input
                ref={firstInputRef}
                id="team-name-input"
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                aria-invalid={nameError ? 'true' : undefined}
                aria-describedby={nameError ? 'team-name-err' : undefined}
                maxLength={255}
              />
              {nameError && (
                <span id="team-name-err" className="dialog-form-field__error" role="alert">
                  {nameError}
                </span>
              )}
            </div>

            {mode === 'create' && (
              <div>
                <label id="team-lead-label" htmlFor="team-lead-search">
                  Team lead{' '}
                  <span aria-hidden="true" style={{ color: 'var(--ut-burnt-orange)' }}>
                    *
                  </span>
                  <span className="label-hint">
                    Required — every new team needs a lead, who becomes its first approver.
                  </span>
                </label>
                <div className="lead-search" ref={leadWrapRef}>
                  {selectedLead !== null && !leadOpen ? (
                    <div className="lead-chip">
                      <span
                        className="avatar"
                        style={{
                          background: avatarColor(selectedLead.userId).bg,
                          color: avatarColor(selectedLead.userId).fg,
                          borderColor: avatarColor(selectedLead.userId).border,
                        }}
                        aria-hidden="true"
                      >
                        {selectedLead.initials}
                      </span>
                      <div className="who">
                        <div className="nm">{selectedLead.displayName}</div>
                        {selectedLead.utEid && <div className="eid">{selectedLead.utEid}</div>}
                      </div>
                      <button
                        type="button"
                        className="lead-chip-change"
                        onClick={() => {
                          setLeadSearch('');
                          setLeadOpen(true);
                          setTimeout(() => leadSearchInputRef.current?.focus(), 0);
                        }}
                      >
                        Change
                      </button>
                      <button
                        type="button"
                        className="lead-chip-clear"
                        aria-label="Clear team lead"
                        onClick={() => {
                          setSelectedLead(null);
                          setLeadUserId('');
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 14 14"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M1 1l12 12M13 1L1 13"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="lead-search-input">
                        <span className="icon" aria-hidden="true">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                            <path
                              d="M9.5 9.5l2.5 2.5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>
                        <input
                          ref={leadSearchInputRef}
                          id="team-lead-search"
                          type="text"
                          placeholder="Search for a lead by name or EID…"
                          value={leadSearch}
                          onChange={(e) => {
                            setLeadSearch(e.target.value);
                            setLeadOpen(true);
                          }}
                          onFocus={() => setLeadOpen(true)}
                          role="combobox"
                          aria-labelledby="team-lead-label"
                          aria-expanded={leadOpen}
                          aria-haspopup="listbox"
                          aria-autocomplete="list"
                          aria-controls={
                            leadOpen && leadResults.length > 0 ? 'lead-search-results' : undefined
                          }
                          aria-activedescendant={
                            leadOpen && leadActiveIndex >= 0
                              ? leadResults[leadActiveIndex]
                                ? `lead-opt-${leadResults[leadActiveIndex].userId}`
                                : undefined
                              : undefined
                          }
                          aria-required="true"
                          aria-invalid={leadError ? 'true' : undefined}
                          aria-describedby={leadError ? 'team-lead-err' : undefined}
                          onKeyDown={(e) => {
                            if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              setLeadOpen(true);
                              if (leadResults.length > 0)
                                setLeadActiveIndex((i) => Math.min(i + 1, leadResults.length - 1));
                            } else if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              if (leadResults.length > 0)
                                setLeadActiveIndex((i) => Math.max(i - 1, 0));
                            } else if (e.key === 'Enter' && leadActiveIndex >= 0) {
                              e.preventDefault();
                              const u = leadResults[leadActiveIndex];
                              if (u) pickLead(u);
                            } else if (e.key === 'Escape') {
                              e.stopPropagation();
                              setLeadOpen(false);
                              setLeadActiveIndex(-1);
                            }
                          }}
                        />
                        {leadSearch && (
                          <button
                            type="button"
                            className="au-clear"
                            aria-label="Clear search"
                            onClick={() => setLeadSearch('')}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 14 14"
                              fill="none"
                              aria-hidden="true"
                            >
                              <path
                                d="M1 1l12 12M13 1L1 13"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                      {leadOpen && leadResults.length > 0 && (
                        <div
                          id="lead-search-results"
                          className="lead-search-pop"
                          role="listbox"
                          aria-label="Lead search results"
                        >
                          {leadResults.map((u, idx) => {
                            const { bg, fg, border } = avatarColor(u.userId);
                            return (
                              <div
                                key={u.userId}
                                id={`lead-opt-${u.userId}`}
                                className={`lead-search-row${idx === leadActiveIndex ? ' hi' : ''}`}
                                role="option"
                                aria-selected={idx === leadActiveIndex}
                                onMouseEnter={() => setLeadActiveIndex(idx)}
                                onClick={() => pickLead(u)}
                              >
                                <span
                                  className="avatar"
                                  style={{ background: bg, color: fg, borderColor: border }}
                                  aria-hidden="true"
                                >
                                  {u.initials}
                                </span>
                                <div className="who">
                                  <div className="nm">{u.displayName}</div>
                                  {u.utEid && <div className="eid">{u.utEid}</div>}
                                </div>
                                <div className="status">
                                  <span className="au-tag pickable">
                                    <svg
                                      width="10"
                                      height="10"
                                      viewBox="0 0 14 14"
                                      fill="none"
                                      aria-hidden="true"
                                    >
                                      <path
                                        d="M7 1v12M1 7h12"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                      />
                                    </svg>
                                    pick
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {leadError && (
                  <span id="team-lead-err" className="dialog-form-field__error" role="alert">
                    {leadError}
                  </span>
                )}
              </div>
            )}

            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 'var(--fw-semibold)',
                  letterSpacing: 'var(--tracking-allcaps)',
                  textTransform: 'uppercase',
                  color: 'var(--fg3)',
                  marginBottom: 8,
                }}
              >
                Color
              </div>
              <div className="color-grid" role="radiogroup" aria-label="Team color">
                {TEAM_COLORS.map((c) => (
                  <button
                    key={c.hex}
                    type="button"
                    role="radio"
                    className={`color-swatch${color === c.hex ? ' selected' : ''}`}
                    aria-label={c.label}
                    aria-checked={color === c.hex}
                    onClick={() => setColor(c.hex)}
                  >
                    <span className="dot" style={{ background: c.hex }} />
                    <span>{c.label}</span>
                    {color === c.hex && (
                      <svg
                        className="ck"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M2.5 7l3 3 6-6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="team-preview" aria-label="Team preview">
              <span className="lbl">Preview</span>
              <span
                className="team-preview-pill"
                style={{
                  color,
                  borderColor: color,
                  background: `${color}18`,
                }}
              >
                <span
                  className="dot"
                  style={{ width: 8, height: 8, borderRadius: '50%', background: color }}
                />
                {teamName.trim() || 'Team name'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--fg3)' }}>{selectedColorLabel}</span>
            </div>

            {mode === 'edit' && (
              <div>
                <label htmlFor="team-lead-select">Team lead</label>
                <select
                  id="team-lead-select"
                  value={leadUserId}
                  onChange={(e) => setLeadUserId(e.target.value)}
                  style={{
                    marginTop: 4,
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-2)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-14)',
                    background: '#fff',
                    color: 'var(--fg1)',
                  }}
                >
                  <option value="">— no lead assigned —</option>
                  {members
                    .slice()
                    .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.displayName}
                        {m.utEid ? ` (${m.utEid})` : ''}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Mounted unconditionally, content toggling: a live region that
                appears in the same commit as its text is announced unreliably in
                VoiceOver — same reason as the delete-team error region below.
                Deliberately one short sentence rather than an aria-live wrapper
                around the fieldset, which would re-read the legend, the help
                text and both options as one uninterruptible block. */}
            <div aria-live="polite" className="sr-only">
              {outgoingLead
                ? `Decision needed: keep or remove ${outgoingLead.displayName}'s approver access.`
                : ''}
            </div>

            {outgoingLead && (
              <fieldset className="admin-decision-fieldset" aria-describedby="outgoing-lead-help">
                <legend>
                  <span className="ic" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                      <path
                        d="M8 7v5M8 5.5v.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  {outgoingLead.displayName} is being replaced as lead
                </legend>
                <p id="outgoing-lead-help" className="admin-decision-fieldset__help">
                  Leads are also team approvers. {outgoingLead.displayName} keeps approver access to
                  this team — including its timesheets and reports — unless you remove it here.
                </p>
                {/* aria-describedby sits on each input as well as the fieldset:
                    whether a screen reader speaks a GROUP's description when you
                    tab to a radio inside it varies by AT, and this description
                    carries the actual consequence. Per-control descriptions are
                    reliably supported. */}
                <label className="admin-decision-fieldset__option">
                  <input
                    type="radio"
                    name="outgoing-lead-scope"
                    aria-describedby="outgoing-lead-help"
                    checked={!revokeOutgoingLeadScope}
                    onChange={() => setRevokeOutgoingLeadScope(false)}
                  />
                  <span>Keep {outgoingLead.displayName} as an approver</span>
                </label>
                <div className="admin-decision-fieldset__group">
                  <label className="admin-decision-fieldset__option">
                    <input
                      type="radio"
                      name="outgoing-lead-scope"
                      aria-describedby="outgoing-lead-help outgoing-lead-revoke-note"
                      checked={revokeOutgoingLeadScope}
                      onChange={() => setRevokeOutgoingLeadScope(true)}
                    />
                    <span>Remove {outgoingLead.displayName}&rsquo;s approver access</span>
                  </label>
                  {/* Outside the <label> on purpose: text inside an associated
                      label folds into the control's accessible NAME, so this
                      would be spoken as part of the option rather than as its
                      description. */}
                  <span id="outgoing-lead-revoke-note" className="admin-decision-fieldset__note">
                    They stay on the team as a contributor.
                  </span>
                </div>
              </fieldset>
            )}
          </div>

          <div className="dialog-footer" style={{ marginTop: 20 }}>
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={isPending || !teamName.trim() || (mode === 'create' && !leadUserId)}
            >
              {isPending ? 'Saving…' : mode === 'create' ? 'Create team' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Add Members Dialog ────────────────────────────────────────────────────────

interface AddMembersDialogProps {
  open: boolean;
  teamName: string;
  currentMemberIds: Set<string>;
  isPending: boolean;
  onClose: () => void;
  onAdd: (selections: { userId: string; role: 'contributor' | 'approver' }[]) => void;
}

function AddMembersDialog({
  open,
  teamName,
  currentMemberIds,
  isPending,
  onClose,
  onAdd,
}: AddMembersDialogProps) {
  const [q, setQ] = useState('');
  // Keyed by userId; the value is the picked search result, initials included.
  const [selected, setSelected] = useState<Map<string, UserDto>>(new Map());
  const dialogRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  useFocusTrap(dialogRef, open);

  const searchQuery = useSearchUsers(q);

  useEffect(() => {
    if (!open) {
      setQ('');
      setSelected(new Map());
      return;
    }
    setTimeout(() => searchInputRef.current?.focus(), 0);
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const results = searchQuery.data ?? [];
  const visibleResults = results.filter((u) => !currentMemberIds.has(u.userId));

  function toggleUser(user: UserDto) {
    if (currentMemberIds.has(user.userId)) return;
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(user.userId)) {
        next.delete(user.userId);
      } else {
        next.set(user.userId, user);
      }
      return next;
    });
  }

  function handleAdd() {
    if (selected.size === 0 || isPending) return;
    onAdd([...selected.keys()].map((userId) => ({ userId, role: 'contributor' as const })));
  }

  return (
    <div
      className="dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-members-dialog-title"
        className="projects-dialog projects-dialog--wide"
        style={{ padding: 0 }}
      >
        <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid var(--border)' }}>
          <h2 id="add-members-dialog-title" className="dialog-title" style={{ marginBottom: 0 }}>
            Add people to <em style={{ fontStyle: 'normal' }}>{teamName}</em>
          </h2>
        </div>

        <div className="add-user-dialog">
          <div className="au-search-row" style={{ position: 'relative' }}>
            <span
              className="icon"
              style={{
                position: 'absolute',
                left: 29,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--fg3)',
                display: 'flex',
                pointerEvents: 'none',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M9.5 9.5l2.5 2.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <input
              ref={searchInputRef}
              type="search"
              placeholder="Search by name or EID…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search users"
              style={{ paddingLeft: 32 }}
            />
          </div>

          <div className="au-filter-row">
            <span style={{ color: 'var(--fg3)', fontSize: 12 }}>
              {visibleResults.length > 0
                ? `${String(visibleResults.length)} result${visibleResults.length === 1 ? '' : 's'}`
                : q.length > 0
                  ? 'No results'
                  : 'Type to search users'}
            </span>
          </div>

          <div className="au-user-list" role="listbox" aria-multiselectable="true">
            {visibleResults.map((u) => {
              const isOnTeam = currentMemberIds.has(u.userId);
              const isSelected = selected.has(u.userId);
              const { bg, fg, border } = avatarColor(u.userId);
              return (
                <div
                  key={u.userId}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isOnTeam}
                  className={`au-user-row${isSelected ? ' selected' : ''}${isOnTeam ? ' disabled' : ''}`}
                  onClick={() => toggleUser(u)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleUser(u);
                    }
                  }}
                  tabIndex={isOnTeam ? -1 : 0}
                >
                  <span
                    className="au-avatar"
                    style={{ background: bg, color: fg, borderColor: border }}
                    aria-hidden="true"
                  >
                    {u.initials}
                  </span>
                  <span className="au-info">
                    <span className="au-name">{u.displayName}</span>
                    <span className="au-eid">{u.utEid ?? u.email}</span>
                  </span>
                  {isOnTeam && <span className="au-already">already on team</span>}
                  {isSelected && !isOnTeam && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden="true"
                      style={{ color: 'var(--ut-burnt-orange)', flexShrink: 0 }}
                    >
                      <path
                        d="M3 8l3.5 3.5 6.5-7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="au-dialog-footer">
          <div className="au-selected-chips">
            {selected.size === 0 && (
              <span style={{ fontSize: 12, color: 'var(--fg3)' }}>No people selected</span>
            )}
            {[...selected.entries()].map(([userId, { displayName, initials }]) => (
              <span key={userId} className="au-chip">
                <span aria-hidden="true">{initials}</span>
                <button
                  type="button"
                  aria-label={`Remove ${displayName}`}
                  onClick={() =>
                    setSelected((prev) => {
                      const next = new Map(prev);
                      next.delete(userId);
                      return next;
                    })
                  }
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      d="M1 1l8 8M9 1L1 9"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </span>
            ))}
          </div>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={selected.size === 0 || isPending}
            onClick={handleAdd}
          >
            {isPending
              ? 'Adding…'
              : `Add ${selected.size > 0 ? String(selected.size) : ''} ${selected.size === 1 ? 'person' : 'people'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Role Editor ──────────────────────────────────────────────────────────────

interface RoleEditorProps {
  userId: string;
  role: 'contributor' | 'approver';
  isLead: boolean;
  onChangeRole: (role: 'contributor' | 'approver') => void;
}

const StarIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <path d="M6 1l1.545 3.13L11 4.635l-2.5 2.435.59 3.44L6 8.885l-3.09 1.625.59-3.44L1 4.635l3.455-.505L6 1z" />
  </svg>
);

function RoleEditor({ userId, role, isLead, onChangeRole }: RoleEditorProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const pillClass = isLead ? 'lead' : role === 'approver' ? 'appr' : 'mem';
  const label = isLead ? 'Lead' : role === 'approver' ? 'Approver' : 'Member';

  return (
    <div className="role-editor" ref={menuRef}>
      <button
        type="button"
        className={`role-pill role-trigger ${pillClass}${open ? ' open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Role: ${label}. Click to change`}
        onClick={() => setOpen((v) => !v)}
        data-user-id={userId}
      >
        {isLead ? <StarIcon /> : null}
        {label}
        <svg
          className="caret"
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            d="M2 3.5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>

      {open && (
        <div className="role-menu" role="menu">
          {isLead && (
            <div className="role-lead-hint">
              <StarIcon />
              <span>
                Team lead — change in <b>Edit team</b>.
              </span>
            </div>
          )}
          <button
            type="button"
            role="menuitem"
            className={`role-opt${!isLead && role === 'approver' ? ' current' : ''}`}
            aria-current={!isLead && role === 'approver' ? 'true' : undefined}
            disabled={isLead}
            onClick={() => {
              if (role !== 'approver') onChangeRole('approver');
              setOpen(false);
            }}
          >
            <span className="ico appr" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <circle cx="6" cy="4" r="2.5" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M1.5 10.5c0-2.5 9-2.5 9 0"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="txt">
              <span className="lbl">Approver</span>
              <span className="sub">Can approve timesheets in this team</span>
            </span>
            {!isLead && role === 'approver' && (
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden="true"
                style={{ color: 'var(--ut-burnt-orange)', marginTop: 4, flexShrink: 0 }}
              >
                <path
                  d="M2.5 7l3 3 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          <button
            type="button"
            role="menuitem"
            className={`role-opt${!isLead && role === 'contributor' ? ' current' : ''}`}
            aria-current={!isLead && role === 'contributor' ? 'true' : undefined}
            disabled={isLead}
            onClick={() => {
              if (role !== 'contributor') onChangeRole('contributor');
              setOpen(false);
            }}
          >
            <span className="ico mem" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <circle cx="6" cy="4" r="2.5" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M1.5 10.5c0-2.5 9-2.5 9 0"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="txt">
              <span className="lbl">Member</span>
              <span className="sub">Can submit timesheets, no approval rights</span>
            </span>
            {!isLead && role === 'contributor' && (
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden="true"
                style={{ color: 'var(--ut-burnt-orange)', marginTop: 4, flexShrink: 0 }}
              >
                <path
                  d="M2.5 7l3 3 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Teams Pane ───────────────────────────────────────────────────────────────

type RoleFilter = 'all' | 'lead' | 'approver' | 'contributor';

export function TeamsPane() {
  const teamsQuery = useAdminTeams();
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [showTeamMeta, setShowTeamMeta] = useState(false);
  const [teamMetaMode, setTeamMetaMode] = useState<'create' | 'edit'>('create');
  const [confirmDeleteTeamId, setConfirmDeleteTeamId] = useState<string | null>(null);
  const [confirmRemoveUserId, setConfirmRemoveUserId] = useState<string | null>(null);
  const confirmDeleteRef = useRef<HTMLDivElement>(null);
  const confirmRemoveRef = useRef<HTMLDivElement>(null);

  useFocusTrap(confirmDeleteRef, confirmDeleteTeamId !== null);
  useFocusTrap(confirmRemoveRef, confirmRemoveUserId !== null);

  const teams = teamsQuery.data ?? [];
  const selectedTeam = teams.find((t) => t.teamId === selectedTeamId) ?? null;

  const membersQuery = useAdminTeamMembers(selectedTeamId);
  const members = membersQuery.data ?? [];

  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const deleteTeam = useDeleteTeam();
  const addTeamMember = useAddTeamMember(selectedTeamId ?? '');
  const removeTeamMember = useRemoveTeamMember(selectedTeamId ?? '');
  const updateMemberRole = useUpdateTeamMemberRole(selectedTeamId ?? '');

  // Select first team automatically when data loads
  useEffect(() => {
    if (selectedTeamId === null && teams.length > 0 && teams[0]) {
      setSelectedTeamId(teams[0].teamId);
    }
  }, [teams, selectedTeamId]);

  // Clear selection when team changes
  useEffect(() => {
    setSelectedIds(new Set());
    setMemberSearch('');
    setRoleFilter('all');
  }, [selectedTeamId]);

  // ── Filtered members ──
  const leadUserId = selectedTeam?.leadUserId ?? null;
  const filteredMembers = members
    .filter((m) => {
      if (roleFilter === 'lead' && m.userId !== leadUserId) return false;
      if (roleFilter === 'approver' && m.role !== 'approver') return false;
      if (roleFilter === 'contributor' && m.role !== 'contributor') return false;
      if (memberSearch) {
        const q = memberSearch.toLowerCase();
        if (
          !m.displayName.toLowerCase().includes(q) &&
          !(m.utEid ?? '').toLowerCase().includes(q) &&
          !m.email.toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      return true;
    })
    .sort((a, b) => {
      // lead first, then approvers, then contributors, alpha within each group
      const aOrder = a.userId === leadUserId ? 0 : a.role === 'approver' ? 1 : 2;
      const bOrder = b.userId === leadUserId ? 0 : b.role === 'approver' ? 1 : 2;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.displayName.localeCompare(b.displayName);
    });

  const currentMemberIds = new Set(members.map((m) => m.userId));

  // ── Counts for filter chips ──
  const approverCount = members.filter((m) => m.role === 'approver').length;
  const contributorCount = members.filter((m) => m.role === 'contributor').length;

  // ── Select all / deselect (lead is never bulk-selectable) ──
  const allFilteredIds = new Set(
    filteredMembers.filter((m) => m.userId !== leadUserId).map((m) => m.userId),
  );
  const allSelected =
    allFilteredIds.size > 0 && [...allFilteredIds].every((id) => selectedIds.has(id));

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allFilteredIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => new Set([...prev, ...allFilteredIds]));
    }
  }

  function toggleSelect(userId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }

  // ── Handlers ──
  const handleCreateTeam = useCallback(
    (data: {
      teamName: string;
      color: string;
      leadUserId: string | null;
      revokeOutgoingLeadScope?: boolean;
    }) => {
      if (!data.leadUserId) return;
      // A new team has no outgoing lead, so the directive is never sent.
      createTeam.mutate(
        { teamName: data.teamName, color: data.color, leadUserId: data.leadUserId },
        {
          onSuccess: (team) => {
            setShowTeamMeta(false);
            setSelectedTeamId(team.teamId);
          },
        },
      );
    },
    [createTeam],
  );

  const handleUpdateTeam = useCallback(
    (data: {
      teamName: string;
      color: string;
      leadUserId: string | null;
      revokeOutgoingLeadScope?: boolean;
    }) => {
      if (!selectedTeamId) return;
      updateTeam.mutate(
        { teamId: selectedTeamId, ...data },
        { onSuccess: () => setShowTeamMeta(false) },
      );
    },
    [updateTeam, selectedTeamId],
  );

  const handleDeleteTeam = useCallback(() => {
    // The button stays focusable while pending (disabling it would drop focus to
    // <body> and strand the error announcement), so guard re-entry here instead.
    if (!confirmDeleteTeamId || deleteTeam.isPending) return;
    deleteTeam.mutate(confirmDeleteTeamId, {
      onSuccess: () => {
        setConfirmDeleteTeamId(null);
        if (selectedTeamId === confirmDeleteTeamId) {
          const next = teams.find((t) => t.teamId !== confirmDeleteTeamId);
          setSelectedTeamId(next?.teamId ?? null);
        }
      },
    });
  }, [deleteTeam, confirmDeleteTeamId, selectedTeamId, teams]);

  const handleAddMembers = useCallback(
    (selections: { userId: string; role: 'contributor' | 'approver' }[]) => {
      if (selections.length === 0) {
        setShowAddMembers(false);
        return;
      }
      void Promise.allSettled(selections.map((sel) => addTeamMember.mutateAsync(sel))).then(() => {
        setShowAddMembers(false);
      });
    },
    [addTeamMember],
  );

  const handleRemoveMember = useCallback(
    (userId: string) => {
      removeTeamMember.mutate(userId, {
        onSuccess: () => {
          setConfirmRemoveUserId(null);
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
          });
        },
      });
    },
    [removeTeamMember],
  );

  const handleBulkRemove = useCallback(() => {
    const ids = filteredMembers.filter((m) => selectedIds.has(m.userId)).map((m) => m.userId);
    if (ids.length === 0) return;
    void Promise.allSettled(ids.map((userId) => removeTeamMember.mutateAsync(userId))).then(() => {
      setSelectedIds(new Set());
    });
  }, [removeTeamMember, selectedIds, filteredMembers]);

  const handleBulkPromote = useCallback(() => {
    const ids = filteredMembers.filter((m) => selectedIds.has(m.userId)).map((m) => m.userId);
    for (const userId of ids) {
      updateMemberRole.mutate({ userId, role: 'approver' });
    }
    setSelectedIds(new Set());
  }, [updateMemberRole, selectedIds, filteredMembers]);

  const handleBulkDemote = useCallback(() => {
    const ids = filteredMembers.filter((m) => selectedIds.has(m.userId)).map((m) => m.userId);
    for (const userId of ids) {
      updateMemberRole.mutate({ userId, role: 'contributor' });
    }
    setSelectedIds(new Set());
  }, [updateMemberRole, selectedIds, filteredMembers]);

  if (teamsQuery.isLoading) {
    return (
      <div style={{ padding: 'var(--space-5)', color: 'var(--fg3)' }} aria-live="polite">
        Loading teams…
      </div>
    );
  }

  return (
    <>
      <div className="admin-shell">
        {/* ── Left sidebar ── */}
        <aside className="admin-sidebar" aria-label="Teams list">
          <div className="admin-sidebar-head">
            <span>Teams</span>
            <span className="ct">{teams.length}</span>
          </div>
          <div className="admin-team-list" role="listbox" aria-label="Teams">
            {teams.map((team) => (
              <button
                key={team.teamId}
                type="button"
                role="option"
                aria-selected={team.teamId === selectedTeamId}
                className={`admin-team-row${team.teamId === selectedTeamId ? ' active' : ''}`}
                onClick={() => setSelectedTeamId(team.teamId)}
              >
                <span className="swatch" style={{ background: team.color }} aria-hidden="true" />
                <span className="info">
                  <span className="nm">{team.teamName}</span>
                  <span className="meta">
                    {team.memberCount} {team.memberCount === 1 ? 'member' : 'members'} ·{' '}
                    {team.approverCount} {team.approverCount === 1 ? 'approver' : 'approvers'}
                  </span>
                </span>
              </button>
            ))}
            {teams.length === 0 && (
              <p style={{ padding: '16px 10px', fontSize: 'var(--fs-14)', color: 'var(--fg3)' }}>
                No teams yet.
              </p>
            )}
          </div>
          <div className="admin-sidebar-foot">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              style={{ width: '100%' }}
              onClick={() => {
                setTeamMetaMode('create');
                setShowTeamMeta(true);
              }}
            >
              + New team
            </button>
          </div>
        </aside>

        {/* ── Right pane ── */}
        <div className="admin-pane">
          {selectedTeam === null ? (
            <div className="admin-pane-empty">
              <p>Select a team from the sidebar, or create a new one.</p>
            </div>
          ) : (
            <TeamRosterPane
              team={selectedTeam}
              members={filteredMembers}
              allMembers={members}
              leadUserId={leadUserId}
              memberSearch={memberSearch}
              onMemberSearchChange={setMemberSearch}
              roleFilter={roleFilter}
              onRoleFilterChange={setRoleFilter}
              approverCount={approverCount}
              contributorCount={contributorCount}
              selectedIds={selectedIds}
              allSelected={allSelected}
              onToggleSelectAll={toggleSelectAll}
              onToggleSelect={toggleSelect}
              onEditTeam={() => {
                setTeamMetaMode('edit');
                setShowTeamMeta(true);
              }}
              onDeleteTeam={() => {
                // Clear any error from a previous attempt so the dialog does not
                // open pre-populated with a stale failure.
                deleteTeam.reset();
                setConfirmDeleteTeamId(selectedTeam.teamId);
              }}
              onAddMembers={() => setShowAddMembers(true)}
              onRemoveMember={(userId) => setConfirmRemoveUserId(userId)}
              onChangeRole={(userId, role) => updateMemberRole.mutate({ userId, role })}
              onBulkRemove={handleBulkRemove}
              onBulkPromote={handleBulkPromote}
              onBulkDemote={handleBulkDemote}
              isLoadingMembers={membersQuery.isLoading}
            />
          )}
        </div>
      </div>

      {/* ── Team meta dialog ── */}
      <TeamMetaDialog
        open={showTeamMeta}
        mode={teamMetaMode}
        initialTeamName={teamMetaMode === 'edit' ? (selectedTeam?.teamName ?? '') : ''}
        initialColor={teamMetaMode === 'edit' ? (selectedTeam?.color ?? '#9cadb7') : '#9cadb7'}
        initialLeadUserId={teamMetaMode === 'edit' ? (selectedTeam?.leadUserId ?? null) : null}
        members={teamMetaMode === 'edit' ? members : []}
        isPending={createTeam.isPending || updateTeam.isPending}
        onClose={() => setShowTeamMeta(false)}
        onSubmit={teamMetaMode === 'create' ? handleCreateTeam : handleUpdateTeam}
      />

      {/* ── Add members dialog ── */}
      {selectedTeam && (
        <AddMembersDialog
          open={showAddMembers}
          teamName={selectedTeam.teamName}
          currentMemberIds={currentMemberIds}
          isPending={addTeamMember.isPending}
          onClose={() => setShowAddMembers(false)}
          onAdd={handleAddMembers}
        />
      )}

      {/* ── Delete team confirmation ── */}
      {confirmDeleteTeamId !== null && (
        <div
          className="dialog-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmDeleteTeamId(null);
          }}
        >
          <div
            ref={confirmDeleteRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="del-team-title"
            aria-describedby={deleteTeam.isError ? 'del-team-err' : undefined}
            className="projects-dialog"
          >
            <h2 id="del-team-title" className="dialog-title">
              Delete team?
            </h2>
            <p style={{ fontSize: 'var(--fs-14)', color: 'var(--fg2)', margin: '0 0 20px' }}>
              This will permanently remove{' '}
              <strong>{teams.find((t) => t.teamId === confirmDeleteTeamId)?.teamName}</strong> and
              all its membership records. Deletion is blocked if the team has any projects assigned
              to it — remove or reassign those projects first.
            </p>
            {/* Mounted unconditionally: a live region that appears in the same
                commit as its text is announced unreliably in VoiceOver. The
                dialog also points aria-describedby here so the message is still
                reachable by Tab if the announcement is missed — matching how the
                other two error spans in this file are wired. */}
            <p
              id="del-team-err"
              className="dialog-form-field__error"
              role="alert"
              style={{ margin: deleteTeam.isError ? '0 0 20px' : 0 }}
            >
              {deleteTeam.isError ? deleteTeam.error.message : ''}
            </p>
            <div className="dialog-footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setConfirmDeleteTeamId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--destructive"
                aria-busy={deleteTeam.isPending}
                onClick={handleDeleteTeam}
              >
                {deleteTeam.isPending ? 'Deleting…' : 'Delete team'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Remove member confirmation ── */}
      {confirmRemoveUserId !== null && (
        <div
          className="dialog-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmRemoveUserId(null);
          }}
        >
          <div
            ref={confirmRemoveRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="del-member-title"
            className="projects-dialog"
          >
            <h2 id="del-member-title" className="dialog-title">
              Remove member?
            </h2>
            <p style={{ fontSize: 'var(--fs-14)', color: 'var(--fg2)', margin: '0 0 20px' }}>
              <strong>{members.find((m) => m.userId === confirmRemoveUserId)?.displayName}</strong>{' '}
              will be removed from <strong>{selectedTeam?.teamName}</strong>. Their app-level role
              will be recalculated based on remaining memberships.
            </p>
            <div className="dialog-footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setConfirmRemoveUserId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--destructive"
                disabled={removeTeamMember.isPending}
                onClick={() => handleRemoveMember(confirmRemoveUserId)}
              >
                {removeTeamMember.isPending ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Team Roster Pane ─────────────────────────────────────────────────────────

interface TeamRosterPaneProps {
  team: AdminTeamDto;
  members: AdminTeamMemberDto[];
  allMembers: AdminTeamMemberDto[];
  leadUserId: string | null;
  memberSearch: string;
  onMemberSearchChange: (q: string) => void;
  roleFilter: RoleFilter;
  onRoleFilterChange: (f: RoleFilter) => void;
  approverCount: number;
  contributorCount: number;
  selectedIds: Set<string>;
  allSelected: boolean;
  onToggleSelectAll: () => void;
  onToggleSelect: (userId: string) => void;
  onEditTeam: () => void;
  onDeleteTeam: () => void;
  onAddMembers: () => void;
  onRemoveMember: (userId: string) => void;
  onChangeRole: (userId: string, role: 'contributor' | 'approver') => void;
  onBulkRemove: () => void;
  onBulkPromote: () => void;
  onBulkDemote: () => void;
  isLoadingMembers: boolean;
}

function TeamRosterPane({
  team,
  members,
  allMembers,
  leadUserId,
  memberSearch,
  onMemberSearchChange,
  roleFilter,
  onRoleFilterChange,
  approverCount,
  contributorCount,
  selectedIds,
  allSelected,
  onToggleSelectAll,
  onToggleSelect,
  onEditTeam,
  onDeleteTeam,
  onAddMembers,
  onRemoveMember,
  onChangeRole,
  onBulkRemove,
  onBulkPromote,
  onBulkDemote,
  isLoadingMembers,
}: TeamRosterPaneProps) {
  const selectedInView = [...selectedIds].filter((id) => members.some((m) => m.userId === id));
  const leadMember = leadUserId ? allMembers.find((m) => m.userId === leadUserId) : null;

  return (
    <>
      {/* Pane header */}
      <div className="admin-pane-head">
        <div className="title-row">
          <h2>
            <span className="swatch lg" style={{ background: team.color }} aria-hidden="true" />
            {team.teamName}
          </h2>
          <div className="pane-actions">
            <button
              type="button"
              className="btn btn--light btn--sm"
              onClick={onEditTeam}
              aria-label={`Edit team: ${team.teamName}`}
              style={{ fontSize: 12, gap: 5 }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="currentColor"
                aria-hidden="true"
              >
                <circle cx="2" cy="6" r="1.25" />
                <circle cx="6" cy="6" r="1.25" />
                <circle cx="10" cy="6" r="1.25" />
              </svg>
              Edit team
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={onDeleteTeam}
              aria-label={`Delete team: ${team.teamName}`}
              style={{ color: 'var(--ut-burnt-orange)', gap: 5 }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path
                  d="M1 1l8 8M9 1L1 9"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              Delete
            </button>
          </div>
        </div>
        <div className="stat-strip">
          <div className="stat lead-stat">
            <div className="l">
              <svg
                width="11"
                height="11"
                viewBox="0 0 12 12"
                fill="currentColor"
                aria-hidden="true"
                style={{ verticalAlign: '-1px' }}
              >
                <path d="M6 1l1.545 3.13L11 4.635l-2.5 2.435.59 3.44L6 8.885l-3.09 1.625.59-3.44L1 4.635l3.455-.505L6 1z" />
              </svg>{' '}
              Lead
            </div>
            <div className="n lead-name">
              {leadMember ? leadMember.displayName : <span className="unset">Not assigned</span>}
            </div>
          </div>
          <div className="stat">
            <div className="n">{allMembers.length}</div>
            <div className="l">Members</div>
          </div>
          <div className="stat">
            <div className="n">{approverCount}</div>
            <div className="l">Approvers</div>
          </div>
          <div className="stat">
            <div className="n">{contributorCount}</div>
            <div className="l">Members-only</div>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedInView.length > 0 && (
        <div className="bulk-bar" role="region" aria-label="Bulk actions">
          <span className="count">
            <b>{selectedInView.length}</b> selected
          </span>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onBulkPromote}>
            Make approvers
          </button>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onBulkDemote}>
            Demote to member
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={onBulkRemove}
            style={{ color: 'var(--danger)' }}
          >
            Remove from team
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="admin-toolbar">
        <div className="search">
          <span className="icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M9.5 9.5l2.5 2.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <input
            type="search"
            placeholder="Search members…"
            value={memberSearch}
            onChange={(e) => onMemberSearchChange(e.target.value)}
            aria-label="Search team members"
          />
        </div>

        <div className="role-filter" role="group" aria-label="Filter by role">
          {(
            [
              { id: 'all', label: 'All', count: allMembers.length },
              { id: 'lead', label: 'Lead', count: leadUserId ? 1 : 0 },
              { id: 'approver', label: 'Approvers', count: approverCount },
              { id: 'contributor', label: 'Members', count: contributorCount },
            ] as const
          ).map((f) => (
            <button
              key={f.id}
              type="button"
              className={`chip-tab${roleFilter === f.id ? ' active' : ''}`}
              aria-pressed={roleFilter === f.id}
              onClick={() => onRoleFilterChange(f.id)}
            >
              {f.label}
              <span className="ct">{f.count}</span>
            </button>
          ))}
        </div>

        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={onAddMembers}
          style={{ marginLeft: 'auto' }}
        >
          + Add members
        </button>
      </div>

      {/* Roster */}
      {isLoadingMembers ? (
        <div
          style={{ padding: 'var(--space-5)', color: 'var(--fg3)', fontSize: 'var(--fs-14)' }}
          aria-live="polite"
        >
          Loading members…
        </div>
      ) : (
        <div className="roster-table" role="table" aria-label={`${team.teamName} members`}>
          {/* Header row */}
          <div className="roster-row head" role="row">
            <div className="cell ck" role="columnheader">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleSelectAll}
                aria-label="Select all visible members"
                disabled={members.length === 0}
              />
            </div>
            <div role="columnheader">Name</div>
            <div role="columnheader">Also on</div>
            <div role="columnheader">Role</div>
            <div className="cell right" role="columnheader">
              Actions
            </div>
          </div>

          {members.length === 0 && (
            <div className="roster-empty" role="row">
              <p>
                {memberSearch || roleFilter !== 'all' ? (
                  'No members match your filter.'
                ) : (
                  <>
                    No members yet.{' '}
                    <button type="button" className="link" onClick={onAddMembers}>
                      Add people to get started.
                    </button>
                  </>
                )}
              </p>
            </div>
          )}

          {members.map((member) => {
            const isSelected = selectedIds.has(member.userId);
            const { bg, fg, border } = avatarColor(member.userId);
            return (
              <RosterRow
                key={member.userId}
                member={member}
                isSelected={isSelected}
                isLead={member.userId === leadUserId}
                avatarColors={{ bg, fg, border }}
                onToggleSelect={() => onToggleSelect(member.userId)}
                onChangeRole={(role) => onChangeRole(member.userId, role)}
                onRemove={() => onRemoveMember(member.userId)}
              />
            );
          })}
        </div>
      )}

      {/* Helper callout */}
      {allMembers.length > 0 && (
        <div className="admin-helper-callout" role="note">
          <span className="ic" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M8 7v5M8 5.5v.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span>
            <b>Role sync:</b> Each person&apos;s app-level role is automatically set to the highest
            role they hold across all teams. Approvers here can approve timesheets from this team.
          </span>
        </div>
      )}
    </>
  );
}

// ─── Roster Row ────────────────────────────────────────────────────────────────

interface RosterRowProps {
  member: AdminTeamMemberDto;
  isSelected: boolean;
  isLead: boolean;
  avatarColors: { bg: string; fg: string; border: string };
  onToggleSelect: () => void;
  onChangeRole: (role: 'contributor' | 'approver') => void;
  onRemove: () => void;
}

function RosterRow({
  member,
  isSelected,
  isLead,
  avatarColors,
  onToggleSelect,
  onChangeRole,
  onRemove,
}: RosterRowProps) {
  const checkId = `roster-check-${member.userId}`;
  return (
    <div
      className={`roster-row${isSelected ? ' selected' : ''}${isLead ? ' is-lead' : ''}`}
      role="row"
      aria-selected={isSelected}
    >
      <div className="cell ck" role="cell">
        <input
          id={checkId}
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          aria-label={`Select ${member.displayName}`}
          disabled={isLead}
        />
      </div>
      <div className="cell name" role="cell">
        <span
          className="avatar"
          style={{
            background: avatarColors.bg,
            color: avatarColors.fg,
            borderColor: avatarColors.border,
          }}
          aria-hidden="true"
        >
          {member.initials}
        </span>
        <span className="who">
          <span className="nm">{member.displayName}</span>
          {member.utEid && <span className="eid">{member.utEid}</span>}
        </span>
      </div>
      <div className="cell other-teams" role="cell">
        {member.otherTeams.length === 0 ? (
          <span className="other-teams-dash">—</span>
        ) : (
          <div className="other-teams-wrap">
            <div className="other-teams-dots" aria-hidden="true">
              {member.otherTeams.slice(0, 4).map((t) => (
                <span key={t.teamId} className="team-dot" style={{ background: t.color }} />
              ))}
              {member.otherTeams.length > 4 && (
                <span className="teams-overflow">+{member.otherTeams.length - 4}</span>
              )}
            </div>
            <span className="sr-only">{member.otherTeams.map((t) => t.teamName).join(', ')}</span>
            <div className="other-teams-tooltip" role="tooltip">
              {member.otherTeams.map((t) => (
                <div key={t.teamId} className="tt-team-row">
                  <span className="dot" style={{ background: t.color }} />
                  {t.teamName}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="cell" role="cell">
        <RoleEditor
          userId={member.userId}
          role={member.role}
          isLead={isLead}
          onChangeRole={onChangeRole}
        />
      </div>
      <div className="cell right" role="cell">
        {!isLead && (
          <button
            type="button"
            className="icon-btn icon-btn--destructive"
            onClick={onRemove}
            aria-label={`Remove ${member.displayName} from team`}
            title={`Remove ${member.displayName}`}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
